"use client";

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { User, AlertCircle, Phone, Check, Loader2 } from 'lucide-react';
import {
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase/client';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { t } = useTranslation('common');

  // Phone auth state
  const [phoneNumber, setPhoneNumber] = useState('+972');
  const [otpCode, setOtpCode] = useState('');
  const [phoneStep, setPhoneStep] = useState<'input' | 'otp' | 'name'>('input');
  const [displayName, setDisplayName] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [showPhoneAuth, setShowPhoneAuth] = useState(false);

  const navigateByRole = async (uid: string) => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    const role = userDoc.exists() ? userDoc.data()?.role : 'client';
    if (role === 'owner' || role === 'admin') {
      router.push('/dashboard');
    } else {
      router.push('/');
    }
  };

  const handleGoogleLogin = async () => {
    if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
      console.log('[Native Auth] Triggering Google Login via React Native Bridge');
      try {
        const win = window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } };
        win.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'PLEASE_LOGIN', provider: 'google' }));
      } catch (e) {
        console.error('WebView postMessage error', e);
      }
      // Works for already compiled TestFlight builds because url.includes('/login') is intercepted
      window.location.href = '/login?triggerNative=1&provider=google';
      return;
    }

    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          name: user.displayName || t('nav.role_client'),
          role: 'client',
          createdAt: new Date()
        });
      }
      
      await navigateByRole(user.uid);
    } catch (err: unknown) {
      console.error(err);
      setError(t('auth.google_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
      console.log('[Native Auth] Triggering Apple Login via React Native Bridge');
      try {
        const win = window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } };
        win.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'PLEASE_LOGIN', provider: 'apple' }));
      } catch (e) {
        console.error('WebView postMessage error', e);
      }
      // Works for already compiled TestFlight builds because url.includes('/login') is intercepted
      window.location.href = '/login?triggerNative=1&provider=apple';
      return;
    }

    setError('');
    setLoading(true);
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    try {
      const result = await signInWithPopup(auth, provider);
      const appleUser = result.user;
      
      const userDoc = await getDoc(doc(db, 'users', appleUser.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', appleUser.uid), {
          uid: appleUser.uid,
          email: appleUser.email,
          name: appleUser.displayName || t('nav.role_client'),
          role: 'client',
          createdAt: new Date()
        });
      }
      
      await navigateByRole(appleUser.uid);
    } catch (err: unknown) {
      console.error(err);
      setError(t('auth.apple_failed', 'Apple Sign-In failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const emailUser = result.user;

      // Ensure user document exists in Firestore (same as Google/Apple)
      const userDoc = await getDoc(doc(db, 'users', emailUser.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', emailUser.uid), {
          uid: emailUser.uid,
          email: emailUser.email,
          name: emailUser.displayName || emailUser.email?.split('@')[0] || t('nav.role_client'),
          role: 'client',
          createdAt: new Date()
        });
      }

      await navigateByRole(emailUser.uid);
    } catch (err: unknown) {
      console.error(err);
      setError(t('auth.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  // ── Phone Auth Flow ──

  const handleSendCode = async () => {
    setError('');
    
    // Validate phone number
    const cleanPhone = phoneNumber.replace(/\s/g, '');
    if (cleanPhone.length < 10) {
      setError(t('auth.phone_login_failed'));
      return;
    }

    setPhoneLoading(true);

    try {
      const response = await fetch('https://requestwhatsappotp-gpngsx2kma-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send code');
      }

      setPhoneStep('otp');
    } catch (err: unknown) {
      console.error('Phone auth error:', err);
      setError(err instanceof Error ? err.message : t('auth.phone_login_failed'));
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (otpCode.length !== 6) return;

    setError('');
    setPhoneLoading(true);

    try {
      const cleanPhone = phoneNumber.replace(/\s/g, '');
      const response = await fetch('https://verifywhatsappotp-gpngsx2kma-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanPhone, code: otpCode })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      // Sign in with custom token
      const result = await signInWithCustomToken(auth, data.customToken);
      const phoneUser = result.user;

      // Check if user has a name
      const userDoc = await getDoc(doc(db, 'users', phoneUser.uid));
      
      if (!userDoc.exists()) {
        // New user — need name
        await setDoc(doc(db, 'users', phoneUser.uid), {
          uid: phoneUser.uid,
          phone: cleanPhone,
          name: phoneUser.displayName || '',
          role: 'client',
          registeredVia: 'phone_auth',
          createdAt: new Date()
        });
      }

      // If user has no displayName, ask for it
      if (!phoneUser.displayName && (!userDoc.exists() || !userDoc.data()?.name)) {
        setPhoneStep('name');
      } else {
        await navigateByRole(phoneUser.uid);
      }
    } catch (err: unknown) {
      console.error('OTP verification error:', err);
      setError(err instanceof Error ? err.message : t('auth.invalid_code'));
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!displayName.trim()) return;
    
    setPhoneLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await updateProfile(currentUser, { displayName: displayName.trim() });
        await setDoc(doc(db, 'users', currentUser.uid), {
          name: displayName.trim(),
        }, { merge: true });
        
        await navigateByRole(currentUser.uid);
      }
    } catch (err: unknown) {
      console.error('Save name error:', err);
      setError(t('auth.phone_login_failed'));
    } finally {
      setPhoneLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center container mx-auto px-4">
      <div className="w-full max-w-md p-8 glass-panel rounded-3xl shadow-xl border border-border">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-4">
            <User className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-center">{t('auth.client_login')}</h1>
          <p className="text-muted-foreground text-center mt-2 font-medium">
            {t('auth.login_subtitle')}
          </p>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded-xl flex items-center gap-3 text-sm font-medium">
            <AlertCircle className="w-5 h-5 shrink-0" />
            {error}
          </div>
        )}

        <button 
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full bg-white text-black border border-gray-200 shadow-sm font-bold py-3.5 rounded-xl mb-4 hover:bg-gray-50 flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {t('auth.google_btn')}
        </button>

        <button 
          onClick={handleAppleLogin}
          disabled={loading}
          className="w-full bg-black text-white border border-gray-800 shadow-sm font-bold py-3.5 rounded-xl mb-4 hover:bg-gray-900 flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          {t('auth.apple_btn', 'Sign in with Apple')}
        </button>

        {/* ── Phone Auth Section ── */}
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground font-bold">{t('auth.or_phone')}</span>
          </div>
        </div>

        {!showPhoneAuth ? (
          <button
            onClick={() => setShowPhoneAuth(true)}
            className="w-full bg-linear-to-r from-emerald-500 to-teal-500 text-white font-bold py-3.5 rounded-xl mb-4 hover:from-emerald-600 hover:to-teal-600 flex items-center justify-center gap-3 transition-all shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
          >
            <Phone className="w-5 h-5" />
            {t('auth.phone_login_title')}
          </button>
        ) : (
          <div className="mb-4 p-4 bg-card border border-border rounded-2xl space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
            {/* Step 1: Phone number input */}
            {phoneStep === 'input' && (
              <>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-emerald-500" />
                  {t('auth.phone_login_title')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={t('auth.phone_placeholder')}
                    dir="ltr"
                    className="flex-1 bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all font-medium text-base"
                  />
                  <button
                    onClick={handleSendCode}
                    disabled={phoneLoading || phoneNumber.replace(/\s/g, '').length < 10}
                    className="px-5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                  >
                    {phoneLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t('auth.send_code')
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: OTP code */}
            {phoneStep === 'otp' && (
              <>
                <div className="text-center mb-2">
                  <p className="text-sm text-emerald-600 font-bold flex items-center justify-center gap-1.5">
                    <Check className="w-4 h-4" />
                    {t('auth.code_sent')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{phoneNumber}</p>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder={t('auth.code_placeholder')}
                    dir="ltr"
                    className="flex-1 bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all font-bold text-center text-xl tracking-[0.3em]"
                    autoFocus
                  />
                  <button
                    onClick={handleVerifyCode}
                    disabled={phoneLoading || otpCode.length !== 6}
                    className="px-5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                  >
                    {phoneLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
                <button
                  onClick={() => { setPhoneStep('input'); setOtpCode(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
                >
                  {t('auth.code_expired')}
                </button>
              </>
            )}

            {/* Step 3: Name */}
            {phoneStep === 'name' && (
              <>
                <div className="text-center mb-2">
                  <p className="text-sm text-emerald-600 font-bold">✅ {t('auth.verify_code')}</p>
                </div>
                <label className="text-sm font-medium">{t('auth.enter_name')}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t('auth.name_placeholder')}
                    className="flex-1 bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all font-medium"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={phoneLoading || !displayName.trim()}
                    className="px-5 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 transition-all disabled:opacity-50 shrink-0"
                  >
                    {phoneLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t('auth.save_name')
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Email Section ── */}
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground font-bold">{t('auth.or_email')}</span>
          </div>
        </div>
        
        <form onSubmit={handleEmailLogin} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{t('auth.email')}</label>
            <input 
              type="email" 
              required
              autoComplete="username email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.email_placeholder')} 
              className="w-full bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all font-medium"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{t('auth.password')}</label>
            <input 
              type="password" 
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" 
              className="w-full bg-background border border-input rounded-xl px-4 py-3 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all font-medium"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-foreground text-background font-bold py-3.5 rounded-xl mt-2 hover:bg-foreground/90 transition-colors disabled:opacity-50"
          >
            {loading ? t('auth.login_loading') : t('auth.login_btn')}
          </button>
        </form>
        
        <div className="mt-8 text-center text-sm font-medium text-muted-foreground flex flex-col gap-2">
          <div>{t('auth.no_account_notice')}</div>
          <div className="mt-4 pt-4 border-t border-border">
            {t('auth.are_you_master')} <Link href="/business/register" className="text-primary font-bold hover:underline">{t('auth.register_business')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
